/**
 * Analytics results pinned to the dashboard.
 *
 * Each completed analysis (stats / filter) lands here as a card at the top
 * of the right panel. Cards are dismissable. Visualisation is picked
 * automatically based on the result shape:
 *
 *   • single value   → big-number KPI tile
 *   • 2+ rows        → horizontal CSS bar list (atlas palette)
 *
 * Designed to feel editorial — section headings in serif, tabular numerals,
 * hairline dividers — to match the rest of the platform.
 */
import { backToNational } from './rightPanel.js';

const HOST_ID = 'analyticsResults';
const MAX_CARDS = 6;

let cards = [];          // newest first: { id, type, layer, payload, response }
let nextId = 1;

const ATLAS = [
  '#04648d', '#98c262', '#4ea3ce', '#e0a93a',
  '#d65a51', '#7a9bbb', '#b8d290', '#5b7d8c'
];

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

export function pushStatsResult(layer, request, response) {
  cards.unshift({ id: nextId++, type: 'stats', layer, request, response });
  if (cards.length > MAX_CARDS) cards.pop();
  render();
  surface();
}

export function pushFilterResult(layer, request, response, savedLayer = null) {
  cards.unshift({ id: nextId++, type: 'filter', layer, request, response, savedLayer });
  if (cards.length > MAX_CARDS) cards.pop();
  render();
  surface();
}

export function pushSpatialJoinResult(source, target, request, response) {
  cards.unshift({ id: nextId++, type: 'join', source, target, request, response });
  if (cards.length > MAX_CARDS) cards.pop();
  render(); surface();
}

export function pushBufferResult(source, target, request, response) {
  cards.unshift({ id: nextId++, type: 'buffer', source, target, request, response });
  if (cards.length > MAX_CARDS) cards.pop();
  render(); surface();
}

export function pushNearestResult(source, target, request, response) {
  cards.unshift({ id: nextId++, type: 'nearest', source, target, request, response });
  if (cards.length > MAX_CARDS) cards.pop();
  render(); surface();
}

export function pushHotspotResult(layer, request, response) {
  cards.unshift({ id: nextId++, type: 'hotspot', layer, request, response });
  if (cards.length > MAX_CARDS) cards.pop();
  render(); surface();
}

export function pushZonalResult(polygons, raster, request, response) {
  cards.unshift({ id: nextId++, type: 'zonal', polygons, raster, request, response });
  if (cards.length > MAX_CARDS) cards.pop();
  render(); surface();
}

export function dismissResult(id) {
  cards = cards.filter(c => c.id !== id);
  render();
}

export function clearResults() { cards = []; render(); }

// ---------------------------------------------------------------------------
// Rendering
// ---------------------------------------------------------------------------

function render() {
  const host = document.getElementById(HOST_ID);
  if (!host) return;
  host.innerHTML = cards.length
    ? `<div class="ar-stack">${cards.map(renderCard).join('')}</div>`
    : '';
}

/** Make sure the right panel is visible and scrolled to show fresh results. */
function surface() {
  // Ensure user is on national view (not detail/compare)
  const detail  = document.getElementById('rp-detail');
  const compare = document.getElementById('rp-compare');
  if ((detail && detail.style.display === 'block') ||
      (compare && compare.style.display === 'block')) {
    backToNational();
  }
  // Open right panel if collapsed
  document.getElementById('rpanel')?.classList.remove('closed');
  // Smooth-scroll to top of right panel
  document.getElementById('rpBody')?.scrollTo({ top: 0, behavior: 'smooth' });
}

function renderCard(card) {
  if (card.type === 'stats')   return renderStatsCard(card);
  if (card.type === 'filter')  return renderFilterCard(card);
  if (card.type === 'join')    return renderJoinCard(card);
  if (card.type === 'buffer')  return renderBufferCard(card);
  if (card.type === 'nearest') return renderNearestCard(card);
  if (card.type === 'hotspot') return renderHotspotCard(card);
  if (card.type === 'zonal')   return renderZonalCard(card);
  return '';
}

// ---- Stats card ----------------------------------------------------------

function renderStatsCard(card) {
  const { id, layer, request, response } = card;
  const metric = request.metric.toUpperCase();
  const title  = request.metric === 'count'
    ? 'Count'
    : `${metric}${request.column ? ` · ${request.column}` : ''}`;
  const subtitle = request.group_by
    ? `${escape(layer.name)} · grouped by ${escape(request.group_by)}`
    : escape(layer.name);

  const body = response.rows.length === 1 && !request.group_by
    ? renderKPI(response.rows[0], request)
    : renderBarList(response.rows, { showGroupValue: true, valueFormatter: fmt });

  return cardShell({ id, title, subtitle, body });
}

function renderKPI(row, request) {
  const v = row.value ?? row.count;
  return `
    <div class="ar-kpi">
      <div class="ar-kpi-value">${fmt(v)}</div>
      <div class="ar-kpi-meta">across ${row.count.toLocaleString()} feature${row.count === 1 ? '' : 's'}</div>
    </div>
  `;
}

// ---- Filter card ---------------------------------------------------------

function renderFilterCard(card) {
  const { id, layer, request, response, savedLayer } = card;
  const conds = (request.conditions || []).map(c => {
    const v = c.op === 'IS NULL' || c.op === 'IS NOT NULL' ? '' : ` ${escape(String(c.value ?? ''))}`;
    return `${escape(c.column)} ${c.op}${v}`;
  }).join(` ${request.combinator || 'AND'} `);

  const title    = savedLayer ? 'Filter · saved as new layer' : 'Filter result';
  const subtitle = `${escape(layer.name)}${conds ? ` · ${conds}` : ''}`;

  const matched = savedLayer ? savedLayer.feature_count : response.matched_count;
  const total   = layer.feature_count ?? null;
  const pct     = total ? (matched / total * 100) : null;

  const body = `
    <div class="ar-kpi">
      <div class="ar-kpi-value">${fmt(matched)}</div>
      <div class="ar-kpi-meta">
        ${total ? `of ${total.toLocaleString()} (${pct.toFixed(1)}%)` : 'features matched'}
        ${savedLayer ? ` · saved as <span style="color:var(--primary);font-weight:600">${escape(savedLayer.name)}</span>` : ''}
      </div>
    </div>
    ${total ? `
      <div class="ar-progress">
        <div class="ar-progress-fill" style="width:${pct.toFixed(1)}%"></div>
      </div>
    ` : ''}
  `;

  return cardShell({ id, title, subtitle, body });
}

// ---- Spatial join card --------------------------------------------------

function renderJoinCard(card) {
  const { id, source, target, request, response } = card;
  const aggLabel = request.aggregate === 'count'
    ? 'Count'
    : `${request.aggregate.toUpperCase()} of ${request.target_column}`;
  const title = `${aggLabel} per feature`;
  const subtitle = `${escape(target?.name || `layer ${request.target_layer_id}`)} ${request.relation} ${escape(source.name)}`;
  const body = response.rows.length === 0
    ? '<div class="ar-kpi"><div class="ar-kpi-meta">No matches.</div></div>'
    : renderBarList(response.rows);
  return cardShell({ id, title, subtitle, body });
}

// ---- Buffer card --------------------------------------------------------

function renderBufferCard(card) {
  const { id, source, target, request, response } = card;
  const title    = target ? `Targets within ${formatDistance(request.radius_m)}` : `Buffer area (${formatDistance(request.radius_m)})`;
  const subtitle = target
    ? `${escape(target.name)} within ${formatDistance(request.radius_m)} of ${escape(source.name)}`
    : `${escape(source.name)} · area in km²`;

  if (target) {
    const summary = `
      <div class="ar-kpi">
        <div class="ar-kpi-value">${response.total_matches.toLocaleString()}</div>
        <div class="ar-kpi-meta">unique target features within range, across ${response.rows.length} source feature${response.rows.length === 1 ? '' : 's'}</div>
      </div>
    `;
    return cardShell({ id, title, subtitle, body: summary + (response.rows.length > 1 ? renderBarList(response.rows) : '') });
  }
  return cardShell({ id, title, subtitle, body: renderBarList(response.rows) });
}

// ---- Nearest distance card ----------------------------------------------

function renderNearestCard(card) {
  const { id, source, target, response } = card;
  const subtitle = `from ${escape(source.name)} → nearest in ${escape(target?.name || '?')}`;
  const summary = `
    <div class="ar-kpi" style="display:flex;gap:24px;align-items:baseline">
      <div>
        <div class="ar-kpi-value">${response.avg_km !== null ? fmtKm(response.avg_km) : '—'}</div>
        <div class="ar-kpi-meta">average</div>
      </div>
      <div style="opacity:.85">
        <div class="ar-kpi-value" style="font-size:24px;color:var(--text)">${response.max_km !== null ? fmtKm(response.max_km) : '—'}</div>
        <div class="ar-kpi-meta">max</div>
      </div>
    </div>
  `;
  return cardShell({
    id, title: 'Nearest distance', subtitle,
    body: summary + (response.rows.length ? renderBarList(response.rows.slice(0, 12).map(r => ({...r, count: 1})), { unitSuffix: ' km' }) : '')
  });
}

// ---- Hotspot card -------------------------------------------------------

function renderHotspotCard(card) {
  const { id, layer, request, response } = card;
  const total = response.rows.length;
  const sig   = response.hot_count + response.cold_count;
  const subtitle = `${escape(layer.name)} · ${escape(request.column)} · K=${request.k}`;

  const summary = `
    <div style="display:grid;grid-template-columns:1fr 1fr 1fr;gap:10px;margin-bottom:10px">
      <div class="ar-kpi" style="padding:0">
        <div class="ar-kpi-value" style="font-size:26px;color:#d65a51">${response.hot_count.toLocaleString()}</div>
        <div class="ar-kpi-meta">hot spots</div>
      </div>
      <div class="ar-kpi" style="padding:0">
        <div class="ar-kpi-value" style="font-size:26px;color:#04648d">${response.cold_count.toLocaleString()}</div>
        <div class="ar-kpi-meta">cold spots</div>
      </div>
      <div class="ar-kpi" style="padding:0">
        <div class="ar-kpi-value" style="font-size:26px;color:var(--text2)">${(total - sig).toLocaleString()}</div>
        <div class="ar-kpi-meta">insignificant</div>
      </div>
    </div>
  `;

  // Show top 6 hot + top 6 cold
  const hotTop  = response.rows.filter(r => r.classification === 'hot').slice(0, 6);
  const coldTop = response.rows.filter(r => r.classification === 'cold').slice(-6);
  const list = hotTop.concat(coldTop).map(r => {
    const color = r.classification === 'hot' ? '#d65a51' : (r.classification === 'cold' ? '#04648d' : '#5b7d8c');
    const z = r.z_score !== null ? r.z_score.toFixed(2) : '—';
    return `
      <div class="ar-bar-row">
        <div class="ar-bar-label">${r.group_value === null ? '<em style="color:var(--text3)">null</em>' : escape(String(r.group_value))}</div>
        <div class="ar-bar-track">
          <div class="ar-bar-fill" style="width:${Math.min(100, Math.abs(r.z_score || 0) * 25)}%;background:${color}"></div>
        </div>
        <div class="ar-bar-value">${fmt(r.value)}</div>
        <div class="ar-bar-count" style="color:${color};font-weight:600">z=${z}</div>
      </div>
    `;
  }).join('');

  const body = summary + (list ? `<div class="ar-bars">${list}</div>` : '');
  return cardShell({ id, title: 'Hotspot analysis', subtitle, body });
}

// ---- Zonal raster card --------------------------------------------------

function renderZonalCard(card) {
  const { id, polygons, raster, request, response } = card;
  const subtitle = `${escape(raster?.name || 'raster')} · band ${request.band} · ${escape(polygons.name)}`;
  if (!response.rows.length) {
    return cardShell({
      id, title: 'Zonal raster stats', subtitle,
      body: '<div class="ar-kpi"><div class="ar-kpi-meta">No polygons.</div></div>'
    });
  }

  const statKeys = request.stats || Object.keys(response.rows[0].stats);
  const head = `<tr>
    <th style="text-align:left">Zone</th>
    ${statKeys.map(k => `<th class="num">${escape(k)}</th>`).join('')}
  </tr>`;
  const rows = response.rows.slice(0, 12).map(r => `
    <tr>
      <td>${r.group_value === null ? '<em style="color:var(--text3)">null</em>' : escape(String(r.group_value))}</td>
      ${statKeys.map(k => `<td class="num">${fmt(r.stats[k])}</td>`).join('')}
    </tr>
  `).join('');

  const overflow = response.rows.length > 12
    ? `<div class="ar-overflow">+ ${response.rows.length - 12} more zones</div>`
    : '';

  const body = `<table class="ar-zonal-table">${head}${rows}</table>${overflow}`;
  return cardShell({ id, title: 'Zonal raster stats', subtitle, body });
}

// ---- Shared shells -------------------------------------------------------

function cardShell({ id, title, subtitle, body }) {
  return `
    <article class="ar-card" data-result-id="${id}">
      <header class="ar-card-head">
        <div style="min-width:0;flex:1">
          <div class="ar-title">${escape(title)}</div>
          <div class="ar-sub">${subtitle}</div>
        </div>
        <button class="ar-dismiss" data-dismiss-result="${id}" title="Dismiss">
          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><path d="M18 6L6 18M6 6l12 12"/></svg>
        </button>
      </header>
      <div class="ar-body">${body}</div>
    </article>
  `;
}

function renderBarList(rows, opts = {}) {
  const max = Math.max(...rows.map(r => Math.abs(r.value ?? 0)), 1);
  const limited = rows.slice(0, 12);
  const suffix = opts.unitSuffix || '';

  const list = limited.map((r, i) => {
    const w = Math.max(2, (Math.abs(r.value ?? 0) / max) * 100);
    const color = ATLAS[i % ATLAS.length];
    const label = r.group_value === null ? '<em style="color:var(--text3)">null</em>' : escape(String(r.group_value));
    return `
      <div class="ar-bar-row">
        <div class="ar-bar-label">${label}</div>
        <div class="ar-bar-track">
          <div class="ar-bar-fill" style="width:${w}%;background:${color}"></div>
        </div>
        <div class="ar-bar-value">${fmt(r.value)}${suffix}</div>
        <div class="ar-bar-count">${r.count > 1 ? `n=${r.count.toLocaleString()}` : ''}</div>
      </div>
    `;
  }).join('');

  const overflow = rows.length > limited.length
    ? `<div class="ar-overflow">+ ${rows.length - limited.length} more</div>`
    : '';

  return `<div class="ar-bars">${list}${overflow}</div>`;
}

function fmtKm(v) { return v === null || v === undefined ? '—' : `${fmt(v)} km`; }
function formatDistance(m) {
  if (m >= 1000) return `${(m / 1000).toLocaleString(undefined, { maximumFractionDigits: 1 })} km`;
  return `${m.toLocaleString()} m`;
}

// ---- Utils ---------------------------------------------------------------

function fmt(v) {
  if (v === null || v === undefined) return '—';
  if (Number.isInteger(v)) return v.toLocaleString();
  return Number(v).toLocaleString(undefined, { maximumFractionDigits: 2 });
}

function escape(s) {
  return String(s ?? '').replace(/[&<>"]/g, c => ({ '&':'&amp;','<':'&lt;','>':'&gt;','"':'&quot;' }[c]));
}

// ---------------------------------------------------------------------------
// Click delegation — dismiss buttons
// ---------------------------------------------------------------------------

document.addEventListener('click', e => {
  const btn = e.target.closest('[data-dismiss-result]');
  if (btn) dismissResult(+btn.dataset.dismissResult);
});
